import React, {
  PropsWithChildren,
  Fragment,
  useContext,
  useEffect,
} from "react";
import { Theme, makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  List,
  ListItem,
  ListSubheader,
  Toolbar,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  Popover,
  ListItemIcon,
  ListItemText,
  Badge,
} from "@material-ui/core";
import {
  AccountCircle,
  Notifications,
  KeyboardArrowDownRounded,
  ExitToAppRounded,
  PersonRounded,
  Assignment,
  AssignmentTurnedIn,
} from "@material-ui/icons";
import { FaUsers } from "react-icons/fa";
import Banner from "../material/images/logo3.png";
import {
  DashboardIcon,
  DesignIcon,
  DocumnetIcon,
  InfomationIcon,
} from "../material/icons";
import { useLocation, useHistory } from "react-router-dom";
import clsx from "clsx";
import { MainContext } from "../context/main";
import { LangContext, changeLang } from "../context/lang";
import { getProjectList } from "../apis/project";

const appBarHeight = 72;
const footerHeight = 24;
const leftWidth = 96;

const useStyle = makeStyles((theme: Theme) => ({
  appBar: {
    height: appBarHeight,
    backgroundColor: theme.palette.secondary.main,
    display: "flex",
  },
  banner: {
    width: leftWidth,
    backgroundColor: theme.palette.primary.main,
    position: "relative",
    display: "flex",
    justifyContent: "center",
  },
  actionBar: {
    width: `calc(100vw - ${leftWidth}px)`,
  },
  toolBar: { height: "100%", justifyContent: "space-between" },
  whiteIcon: { color: theme.palette.common.white },
  marginRight: { marginRight: theme.spacing(1) },
  areaContent: {
    // height: `calc(100vh - ${appBarHeight}px)`,
    position: "relative",
  },
  leftContent: {
    width: leftWidth,
    backgroundColor: "#E5EAEE",
    position: "absolute",
    left: 0,
    height: "100%",
  },
  default: { color: theme.palette.secondary.main },
  select: { color: theme.palette.primary.main },
  routeText: {
    // fontFamily: "Montserrat-Medium",
    fontWeight: 500,
    width: "100%",
    textAlign: "center",
  },
  routeList: {
    padding: `${theme.spacing(3)}px ${theme.spacing(1) / 2}px`,
  },
  rightContent: {
    width: `calc(100% - ${leftWidth}px)`,
    paddingLeft: leftWidth,
  },
  childrenContent: {
    height: `calc(100vh - ${appBarHeight + footerHeight}px)`,
    position: "relative",
    backgroundColor: "#F4F7F9",
  },
  footer: {
    height: footerHeight,
    backgroundColor: "#E5EAEE",
    display: "flex",
    justifyContent: "center",
  },
  flexMiddle: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  errorColor: { color: theme.palette.error.main },
  divNoti: {
    width: theme.spacing(40),
    padding: theme.spacing(0.5),
    maxHeight: theme.spacing(30),
    overflowY: "auto",
  },
  primaryColor: { color: theme.palette.primary.main },
  subHeaderNoti: {
    background: `#ffffffe8`,
  },
  projectTitle: { color: theme.palette.common.white },
  langBtn: { cursor: "pointer" },
}));

const LayoutApp = (props: PropsWithChildren<any>) => {
  const classes = useStyle();
  const location = useLocation();
  const currentPath = location.pathname;
  const history = useHistory();
  const { userInfo, setuserInfo, setsaveDesign } = useContext(MainContext);
  const userRole = userInfo?.role;
  const { lang, dispatchLang } = useContext(LangContext);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notiEl, setnotiEl] = React.useState<HTMLButtonElement | null>(null);
  const [notiList, setnotiList] = React.useState<any[]>([]);

  const handleClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const _signout = () => {
    localStorage.clear();
    setuserInfo(undefined);
    history.push("/auth/signin");
  };

  const _changeView = (pathName: string) => () => {
    if (currentPath === "/app/design/project-drawing") {
      setsaveDesign(pathName);
    } else {
      history.push(pathName);
    }
  };

  useEffect(() => {
    const aboutController = new AbortController();
    if (
      userRole &&
      Boolean(userRole === "officer" || userRole === "officer_head")
    ) {
      getProjectList(undefined, aboutController)
        .then((rs) => {
          rs.data &&
            setnotiList(
              rs.data.filter(
                (noti: any) => noti.status === 0 || noti.status === 2
              )
            );
        })
        .catch((error: any) => {
          console.log(error);
        });
    }
    return () => {
      if (
        userRole &&
        Boolean(userRole === "officer" || userRole === "officer_head")
      ) {
        aboutController.abort();
      }
    };
  }, [currentPath, userRole]);

  const renderWaittingProject = () => {
    const waittingProject = notiList.filter((noti: any) => noti.status === 0);
    if (waittingProject.length === 0) {
      return (
        <ListItem>
          <ListItemText secondary={`${lang.data.noProject}`} />
        </ListItem>
      );
    } else {
      return waittingProject.map((noti: any) => (
        <ListItem button key={noti._id}>
          <ListItemIcon>
            <Assignment color="error" />
          </ListItemIcon>
          <ListItemText
            primary={`${lang.data.workOrderNo} ${noti.work_order_no}`}
            secondary={`${lang.data.lastUpdate} ${new Date(
              noti.updated_at
            ).toLocaleDateString()}`}
          />
        </ListItem>
      ));
    }
  };

  const renderCompleteProject = () => {
    const completeProject = notiList.filter((noti: any) => noti.status === 2);
    if (completeProject.length === 0) {
      return (
        <ListItem>
          <ListItemText secondary={`${lang.data.noProject}`} />
        </ListItem>
      );
    } else {
      return completeProject.map((noti: any) => (
        <ListItem button key={noti._id}>
          <ListItemIcon>
            <AssignmentTurnedIn
              color="primary"
              className={classes.primaryColor}
            />
          </ListItemIcon>
          <ListItemText
            primary={`${lang.data.workOrderNo} ${noti.work_order_no}`}
            secondary={`${lang.data.lastUpdate} ${new Date(
              noti.updated_at
            ).toLocaleDateString()}`}
          />
        </ListItem>
      ));
    }
  };

  return (
    <Fragment>
      <Popover
        open={Boolean(notiEl)}
        anchorEl={notiEl}
        onClose={() => {
          setnotiEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <div className={classes.divNoti}>
          <List
            component="nav"
            aria-labelledby="waitting-list-subheader"
            classes={{ subheader: classes.subHeaderNoti }}
            subheader={
              <ListSubheader component="div" id="waitting-list-subheader">
                {lang.data.waitting}
              </ListSubheader>
            }
          >
            {renderWaittingProject()}
          </List>
          <List
            component="nav"
            classes={{ subheader: classes.subHeaderNoti }}
            aria-labelledby="complete-list-subheader"
            subheader={
              <ListSubheader component="div" id="complete-list-subheader">
                {lang.data.complete}
              </ListSubheader>
            }
          >
            {renderCompleteProject()}
          </List>
        </div>
      </Popover>
      <div className={classes.appBar}>
        <div className={classes.banner}>
          <div className={classes.flexMiddle}>
            <img src={Banner} alt="banner" width={68} />
          </div>
        </div>
        <div className={classes.actionBar}>
          <Toolbar className={classes.toolBar}>
            <Typography variant="h6" className={classes.projectTitle}>
              {lang.data.project}
            </Typography>
            <div style={{ display: "flex" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingRight: 8,
                }}
              >
                <Typography
                  variant="body1"
                  style={{
                    color: "#ffffff",
                    fontFamily:
                      lang.locale === "en"
                        ? "Montserrat-Bold"
                        : "Kanit-Regular",
                  }}
                  className={classes.langBtn}
                  onClick={() => {
                    dispatchLang(changeLang("en"));
                  }}
                >
                  EN
                </Typography>
                <Typography
                  variant="body1"
                  style={{
                    color: "#ffffff",
                    paddingRight: 4,
                    paddingLeft: 4,
                    cursor: "default",
                  }}
                >
                  |
                </Typography>
                <Typography
                  variant="body1"
                  style={{
                    color: "#ffffff",
                    fontFamily:
                      lang.locale === "th"
                        ? "Montserrat-Bold"
                        : "Kanit-Regular",
                  }}
                  className={classes.langBtn}
                  onClick={() => {
                    dispatchLang(changeLang("th"));
                  }}
                >
                  TH
                </Typography>
              </div>
              {/* <IconButton onClick={_changeLang}>
                <GTranslate className={classes.whiteIcon} />
              </IconButton> */}
              {Boolean(
                userRole === "officer" || userRole === "officer_head"
              ) && (
                <IconButton
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    setnotiEl(event.currentTarget);
                  }}
                >
                  <Badge badgeContent={notiList.length} color="primary">
                    <Notifications className={classes.whiteIcon} />
                  </Badge>
                </IconButton>
              )}

              <Button
                variant="text"
                style={{ textTransform: "unset" }}
                onClick={handleClickMenu}
              >
                {userInfo?.profile_picture ? (
                  <Avatar
                    src={userInfo?.profile_picture}
                    className={classes.marginRight}
                  />
                ) : (
                  <AccountCircle
                    fontSize="large"
                    className={clsx(classes.marginRight, classes.whiteIcon)}
                  />
                )}

                <Typography variant="body1" className={classes.whiteIcon}>
                  {userInfo?.first_name}
                </Typography>
                <KeyboardArrowDownRounded
                  className={classes.whiteIcon}
                  style={{ marginLeft: 8 }}
                />
              </Button>
              <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
              >
                <MenuItem
                  onClick={() => {
                    history.push("/app/profile");
                  }}
                >
                  <PersonRounded className={classes.marginRight} />
                  <Typography variant="body1">{lang.data.profile}</Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={_signout}>
                  <ExitToAppRounded
                    className={clsx(classes.marginRight, classes.errorColor)}
                  />
                  <Typography variant="body1" color="primary">
                    {lang.data.logout}
                  </Typography>
                </MenuItem>
              </Menu>
            </div>
          </Toolbar>
        </div>
      </div>
      <div className={classes.areaContent}>
        <div className={classes.leftContent}>
          <List>
            <ListItem
              button
              className={clsx(classes.routeList, classes.flexMiddle)}
              onClick={_changeView("/app/dashboard")}
            >
              <DashboardIcon
                className={clsx(
                  location.pathname === "/app/dashboard"
                    ? classes.select
                    : classes.default
                )}
              />
              <Typography
                variant="body2"
                className={clsx(
                  location.pathname === "/app/dashboard"
                    ? classes.select
                    : classes.default,
                  classes.routeText
                )}
              >
                {lang.data.dashboard}
              </Typography>
            </ListItem>

            {userInfo?.role === "admin" || userInfo?.role === "root" ? (
              <ListItem
                button
                className={clsx(classes.routeList, classes.flexMiddle)}
                onClick={_changeView("/app/user")}
              >
                <FaUsers
                  size={26}
                  className={clsx(
                    location.pathname === "/app/user"
                      ? classes.select
                      : classes.default
                  )}
                />
                <Typography
                  variant="body2"
                  className={clsx(
                    location.pathname === "/app/user"
                      ? classes.select
                      : classes.default,
                    classes.routeText
                  )}
                >
                  {lang.data.user}
                </Typography>
              </ListItem>
            ) : null}

            <ListItem
              button
              className={clsx(classes.routeList, classes.flexMiddle)}
              onClick={_changeView("/app/design")}
            >
              <DesignIcon
                className={clsx(
                  location.pathname.includes("/app/design")
                    ? classes.select
                    : classes.default
                )}
              />
              <Typography
                variant="body2"
                className={clsx(
                  location.pathname.includes("/app/design")
                    ? classes.select
                    : classes.default,
                  classes.routeText
                )}
              >
                {lang.data.design}
              </Typography>
            </ListItem>
            <ListItem
              button
              className={clsx(classes.routeList, classes.flexMiddle)}
              onClick={_changeView("/app/document")}
            >
              <DocumnetIcon
                className={clsx(
                  location.pathname === "/app/document"
                    ? classes.select
                    : classes.default
                )}
              />
              <Typography
                variant="body2"
                className={clsx(
                  location.pathname === "/app/document"
                    ? classes.select
                    : classes.default,
                  classes.routeText
                )}
              >
                {lang.data.document}
              </Typography>
            </ListItem>
            <ListItem
              button
              className={clsx(classes.routeList, classes.flexMiddle)}
              onClick={_changeView("/app/information")}
            >
              <InfomationIcon
                className={clsx(
                  location.pathname === "/app/information"
                    ? classes.select
                    : classes.default
                )}
              />
              <Typography
                variant="body2"
                className={clsx(
                  location.pathname === "/app/information"
                    ? classes.select
                    : classes.default,
                  classes.routeText
                )}
              >
                {lang.data.information}
              </Typography>
            </ListItem>
          </List>
        </div>
        <div className={classes.rightContent}>
          <div className={classes.childrenContent}>{props.children}</div>
          <div className={classes.footer}>
            <Typography
              variant="caption"
              component="span"
              style={{ marginRight: 4 }}
            >
              © 2020
            </Typography>
            <Typography variant="caption" component="span" color="primary">
              {lang.data.bandname}
            </Typography>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default LayoutApp;
